/*
Reusable confirmation dialog that replaces window.confirm() across
the SPA. Renders a small modal with a title, a message, an
optional emphasised "entity" line (the name of the row being
deleted, the recipient being revoked, …) so the user can sanity-
check what they're about to do, and explicit Confirm / Cancel
buttons. The destructive button is styled with the danger colour.

Keyboard contract:
  - Escape   → cancel
  - Enter    → confirm (focused on the destructive button)
  - clicking the backdrop → cancel
The first render auto-focuses the destructive button so a stray
double-tap doesn't slip past, but Escape always wins.

Usage:
  const dialog = useConfirmDialog();
  if (await dialog.confirm({
        title: "Supprimer la carte ?",
        entity: card.serialNumber,
        message: "Cette action est irréversible.",
        confirmLabel: "Supprimer",
      })) {
    // user confirmed
  }
*/

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

/*
Options accepted by useConfirmDialog().confirm().
*/
export interface ConfirmOptions
{
	title:			string;
	message?:		ReactNode;
	entity?:		string;					// Highlighted line (serial number / email / …)
	confirmLabel?:	string;					// Default: "Confirmer"
	cancelLabel?:	string;					// Default: "Annuler"
	destructive?:	boolean;				// Default: true (most call sites delete)
}

/*
Opaque return value of the hook: a `confirm()` thenable plus the
JSX node the consumer must render somewhere in the tree.
*/
export interface ConfirmDialogHandle
{
	confirm:	(inOpts: ConfirmOptions) => Promise<boolean>;
	dialog:		ReactNode;
}

interface InternalState
{
	options:	ConfirmOptions;
	resolve:	(inOk: boolean) => void;
}

/*
Hook that returns the confirm() function plus the JSX to render.
Centralising this in a hook (rather than a global imperative
"confirmDialog.show()") plays nicely with React 18 strict mode and
keeps each instance scoped to one consumer.
*/
export function useConfirmDialog(): ConfirmDialogHandle
{
	const { t } = useTranslation("common");
	const [fState, fSetState] = useState<InternalState | null>(null);
	const fConfirmBtnRef = useRef<HTMLButtonElement | null>(null);

	const fConfirm = useCallback((inOpts: ConfirmOptions): Promise<boolean> =>
	{
		return new Promise((vResolve) =>
		{
			fSetState({ options: inOpts, resolve: vResolve });
		});
	}, []);

	const fHandleClose = useCallback((inOk: boolean) =>
	{
		if (fState !== null)
		{
			fState.resolve(inOk);
			fSetState(null);
		}
	}, [fState]);

	// Auto-focus the destructive button when the dialog opens; Escape
	// always cancels.
	useEffect(() =>
	{
		if (fState === null)
		{
			return undefined;
		}

		fConfirmBtnRef.current?.focus();

		const fHandleKey = (vEvent: KeyboardEvent) =>
		{
			if (vEvent.key === "Escape")
			{
				vEvent.preventDefault();
				fHandleClose(false);
			}
		};

		window.addEventListener("keydown", fHandleKey);

		return () => window.removeEventListener("keydown", fHandleKey);
	}, [fState, fHandleClose]);

	const vDialog: ReactNode = fState === null
		? null
		: (
			<div
				className="modal-backdrop"
				onClick={() => fHandleClose(false)}
				role="presentation"
			>
				<div
					className="modal-dialog"
					onClick={(vEvent) => vEvent.stopPropagation()}
					role="dialog"
					aria-modal="true"
					aria-labelledby="confirm-title"
				>
					<h2 id="confirm-title" style={{ marginTop: 0 }}>{fState.options.title}</h2>
					{fState.options.entity !== undefined
						? <p className="modal-dialog__entity"><code>{fState.options.entity}</code></p>
						: null}
					{fState.options.message !== undefined
						? <p className="muted">{fState.options.message}</p>
						: null}
					<div className="modal-dialog__buttons">
						<button
							type="button"
							className="button button--ghost"
							onClick={() => fHandleClose(false)}
						>
							{fState.options.cancelLabel ?? t("cancel")}
						</button>
						<button
							ref={fConfirmBtnRef}
							type="button"
							className={`button ${(fState.options.destructive ?? true) ? "button--danger" : ""}`}
							onClick={() => fHandleClose(true)}
						>
							{fState.options.confirmLabel ?? t("confirm")}
						</button>
					</div>
				</div>
			</div>
		);

	return { confirm: fConfirm, dialog: vDialog };
}
